import React, { useState } from "react";
import { SERVICES } from "../../../constants/helpServices";
import { clearFieldError } from "../../../utils/handleErrors";
import { Button, Tab } from "../../common";
import { DropdownComponent, InputComponent } from "../Forms";
import { validateSupportForm } from "../../../modules/profile";
import { ICreateSupport } from "../../../@types";
import UploadIcon from "../../../assets/icons/UploadIcon";
import { CloseIcon } from "../../../assets/icons";
import { shortenFileName } from "../../../utils";
import { useCustomToast } from "../../../utils/toastUtils";

interface ReportBugProps {
  showSideDrawer: boolean;
  onClose?: () => void;
  onSubmit: (supportData: ICreateSupport) => void;
}

const ReportBug: React.FC<ReportBugProps> = (props) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedValue, setSelectedValue] = useState("");
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [file, setFile] = React.useState<File | null>();
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const { showToastWithProps } = useCustomToast();

  const initialFormData = {
    feature: "",
    subject: "",
    description: "",
    type: "complaint",
    imageUrl: "",
  };

  const [formData, setFormData] = useState({
    feature: "",
    subject: "",
    description: "",
    type: "complaint",
    imageUrl: "",
  });

  const [validationResults, setValidationResults] = useState({
    feature: "",
    subject: "",
    description: "",
    type: "complaint",
    imageUrl: "",
  });

  const handleSelectFile = () => {
    inputRef.current?.click();
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64Image = reader.result as string;
        setFile(selectedFile);
        setFormData((prevFormData) => ({
          ...prevFormData,
          imageUrl: base64Image,
        }));
        setImagePreview(base64Image);
      };

      reader.readAsDataURL(selectedFile);
    } else {
      setFile(null);
      setFormData((prevFormData) => ({
        ...prevFormData,
        imageUrl: "",
      }));
      setImagePreview(null);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const errors = validateSupportForm(formData);
      if (Object.keys(errors).some((key) => errors[key])) {
        setValidationResults(errors);
        return;
      }

      const supportData = {
        feature: formData.feature,
        subject: formData.subject,
        description: formData.description,
        type: "complaint",
        imageUrl: formData.imageUrl,
      };

      setIsSubmitted(true);
      await props.onSubmit(supportData);
      setFile(null);
      setFormData(initialFormData);
      setImagePreview(null);
    } catch (error) {
      if (error instanceof Error) {
        showToastWithProps({
          message: error.message || "Something went wrong",
          type: "error",
        });
      } else {
        // If error is not an instance of Error, fall back to a generic message
        showToastWithProps({
          message: "Something went wrong",
          type: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (e: { preventDefault: any }) => {
    e.preventDefault;
    setIsOpen(false);
    props.onClose && props.onClose();
  };

  const dropDownArray = SERVICES;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setValidationResults({
      ...validationResults,
      [name]: "",
    });
    clearFieldError(name, validationResults, setValidationResults);
  };

  const handleDropDownChange = (name: string, value: string) => {
    clearFieldError(name, validationResults, setValidationResults);
    setFormData({ ...formData, [name]: value });
  };

  return (
    <div className="h-full overflow-auto scrollbar-hide">
      <div>
        <h1 className="font-semibold text-lg">Report a Bug</h1>
        <p>
          Anything you are missing in our product? Kindly drop <br /> a message
          here to let us know!
        </p>
      </div>
      <form onSubmit={(event) => event.preventDefault()}>
        <div className=" font-light text-[16px] "></div>

        <div className="">
          <label
            htmlFor="Select "
            className="block font-sm text-[16px] mt-[2em] text-black mb-2"
          >
            Select the page you have an issue with{" "}
            <span className="text-red-600">*</span>
          </label>
          <DropdownComponent
            name="feature"
            options={dropDownArray}
            error={validationResults.feature}
            placeholder="Click to Select"
            selectedValue={formData.feature}
            value={formData.feature}
            onChange={handleDropDownChange}
            isSubmitted={isSubmitted}
            dropdownClassName="w-[100%] lg:bg-white  rounded py-1 focus:ring-0 pointer-events placeholder-gray-400 !border-[#D0D5DD] placeholder-opacity-75"
            iconClassName="mt-[2px] "
          />
          {validationResults.feature && (
            <span className=" text-red-500">{validationResults.feature}</span>
          )}
        </div>
        <div className="mt-[2em] ">
          <label
            htmlFor="Title"
            className="block font-sm text-[16px] text-black "
          >
            Title <span className="text-red-600">*</span>
          </label>
          <InputComponent
            placeholder="Kindly Share"
            type="text"
            name="subject"
            value={formData.subject}
            handleChange={handleInputChange}
            error={validationResults.subject}
            className="w-full border-[#D0D5DD]"
          />
        </div>
        <div className="mt-[2em]">
          <label
            htmlFor="image"
            className="block font-sm text-[16px] text-black mb-2"
          >
            Bug Image
          </label>
          {/* <p className="text-[12px] mb-2">
            Feel free to add an image that describes the bug
          </p> */}
          <div className="w-full h-[120px] bg-opacity-[0.08] flex flex-col justify-center items-center bg-gray-300 border border-dotted border-gray pb-4  ">
            {file && (
              <div className="flex items-center justify-center text-lg mt-1">
                <span className="text-[#0500F9] mx-3 font-medium">
                  {shortenFileName(file)}
                </span>
                <a href="#close" onClick={() => setFile(undefined)}>
                  <CloseIcon color="red" />
                </a>
              </div>
            )}
            <div className="flex justify-center mt-[13px]">
              <button
                onClick={handleSelectFile}
                className="flex items-center  px-[13px] py-[8px] rounded-[4px]  "
              >
                {/* <p>Upload document</p> */}
                <div className=" "></div>
                <UploadIcon />
              </button>
              <input
                className="hidden"
                type="file"
                ref={inputRef}
                accept="jpg,jpeg,png"
                onChange={handleUpload}
              />
            </div>

            <div className=" text-center leading-5">
              <p className="font-semibold">Upload Document </p>
              <p className="mt -2"> JPEG, XLS or CSV document </p>
            </div>
          </div>
        </div>
        <div className=" mt-[2em]  ">
          <label
            htmlFor="description"
            className="block font-sm text-[16px] text-black my-[10px]"
          >
            Describe the bug experienced
          </label>
          <textarea
            value={formData.description}
            className={`border min-h-[123px] border-[#D0D5DD]  h-[80px] w-full rounded-md resize-y overflow-y-auto word-wrap break-word `}
            name="description"
            placeholder="Please Share"
            onChange={(e: any) => handleInputChange(e)}
          />
          {validationResults.description && (
            <span className="text-red-500">
              {validationResults.description}
            </span>
          )}
        </div>
        <div className="w-full h-full flex  pt-[20px] pb-[40px]">
          <div className="w-full">
            <Button
              text={isLoading ? "Submitting..." : "Submit"}
              isLoading={isLoading}
              onClick={handleSubmit}
              disabled={isLoading}
              size="full"
              variant="secondary"
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export { ReportBug };
